import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';

// AgriLinkModule_AgriMarket.jsx
// Mobile-first responsive React component (single-file) using Tailwind CSS
// Dummy data included. Integrate with backend later by replacing dummy arrays and submit handlers.

export default function AgriLinkCommodityWholesale() {
  const MAIN_TABS = [
    "Collection Center",
    "Wholesale",
    "Import",
    "Export",
    "Guess Price",
  ];

  const UNIT_OPTIONS = ["kg", "quintal", "tonne", "litre", "unit"];

  const categories = [
    {
      id: "cereals",
      name: "Cereals & Millets",
      icon: "🌾",
      examples: ["Rice", "Wheat", "Maize", "Barley", "Oats", "Sorghum", "Millets"],
    },
    {
      id: "pulses",
      name: "Pulses",
      icon: "🫘",
      examples: ["Lentils", "Chickpeas", "Pigeon Pea", "Black Gram", "Green Gram", "Kidney Beans"],
    },
    {
      id: "oilseeds",
      name: "Oilseeds",
      icon: "🌻",
      examples: ["Groundnut", "Soybean", "Mustard", "Sunflower", "Sesame"],
    },
    { id: "fruits", name: "Fruits", icon: "🍓", examples: ["Mango", "Banana", "Apple", "Grapes", "Pomegranate", "Papaya"] },
    { id: "vegetables", name: "Vegetables", icon: "🥬", examples: ["Potato", "Onion", "Tomato", "Cauliflower", "Brinjal", "Okra"] },
    { id: "spices", name: "Spices", icon: "🌶️", examples: ["Turmeric", "Cumin", "Black Pepper", "Cardamom", "Coriander", "Clove"] },
    { id: "plantation", name: "Plantation Crops", icon: "☕", examples: ["Tea", "Coffee", "Rubber", "Cocoa", "Coconut"] },
    { id: "commercial", name: "Commercial / Cash Crops", icon: "💰", examples: ["Sugarcane", "Cotton", "Tobacco", "Jute"] },
    { id: "livestock", name: "Livestock & Animal", icon: "🐄", examples: ["Milk", "Meat", "Eggs", "Wool"] },
    { id: "fisheries", name: "Fisheries & Aquaculture", icon: "🐟", examples: ["Shrimp", "Prawn", "Rohu", "Catla", "Tilapia"] },
    { id: "forestry", name: "Forestry", icon: "🌲", examples: ["Timber", "Bamboo", "Honey", "Resin"] },
    { id: "fodder", name: "Fodder & Feed", icon: "🌱", examples: ["Lucerne", "Berseem", "Napier Grass", "Maize Silage"] },
    { id: "medicinal", name: "Medicinal & Aromatic", icon: "🌿", examples: ["Aloe Vera", "Tulsi", "Ashwagandha", "Lemongrass"] },
    { id: "processed", name: "Processed Agri", icon: "🧃", examples: ["Dehydrated Fruits", "Fruit Pulp", "Cold-Pressed Oils", "Flour", "Spice Powders"] },
  ];

  const countries = [
    { code: "IN", name: "India", markets: ["Mumbai Mandai", "Delhi Azadpur", "Kolkata New Market", "Bengaluru Market"] },
    { code: "US", name: "USA", markets: ["Chicago Produce Market", "Los Angeles Market"] },
    { code: "CN", name: "China", markets: ["Beijing Market", "Shanghai Market"] },
  ];

  const collectionCentersData = [
    {
      country: "India",
      places: [
        { place: "Maharashtra", centers: ["Palava Collection Center", "Nashik Cold Store"] },
        { place: "Karnataka", centers: ["Bengaluru Farm Hub", "Mysore Aggregator"] },
      ],
    },
    {
      country: "Bangladesh",
      places: [{ place: "Dhaka", centers: ["Dhaka Central Collection"] }],
    },
  ];

  const dummyPriceList = [
    { commodity: "Mango", market: "Mumbai Mandai", country: "India", unit: "kg", price: 80 },
    { commodity: "Rice", market: "Delhi Azadpur", country: "India", unit: "quintal", price: 2500 },
    { commodity: "Lentils", market: "Kolkata New Market", country: "India", unit: "kg", price: 90 },
    { commodity: "Soybean", market: "Chicago Produce Market", country: "USA", unit: "tonne", price: 42000 },
  ];

  const dummyWinners = [
    { name: "Anita Sharma", commodity: "Mango", market: "Mumbai Mandai", date: "2025-09-12", prize: "₹5,000" },
    { name: "Ravi Kumar", commodity: "Rice", market: "Delhi Azadpur", date: "2025-09-20", prize: "₹3,000" },
  ];

  // State
  const [mainTab, setMainTab] = useState(MAIN_TABS[0]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedCommodity, setSelectedCommodity] = useState(selectedCategory.examples[0]);
  const [selectedAction, setSelectedAction] = useState(null); // Bulk Buy, Bulk Sell, Future Order, Price List, Guess Price, Winners

  // Forms state for buy/sell/future
  const [tradeForm, setTradeForm] = useState({
    buyerName: "",
    sellerName: "",
    address: "",
    contactNumber: "",
    email: "",
    qty: "",
    unit: UNIT_OPTIONS[0],
    sourceCountry: "",
    destinationCountry: "",
    sourcePort: "",
    destinationPort: "",
    tentativeDeliveryDate: "",
    remarks: "",
  });

  // Collection center form state
  const [colForm, setColForm] = useState({ country: "India", place: "Maharashtra", center: "Palava Collection Center", name: "", role: "Farmer", address: "", contactNumber: "", email: "", remarks: "" });

  // Guess Price form state
  const [guessForm, setGuessForm] = useState({ country: countries[0].code, market: countries[0].markets[0], guessRate: "", guessPerUnit: "", variation: "" });

  const [priceList] = useState(dummyPriceList);
  const [winners] = useState(dummyWinners);

  // compute first day of next month string YYYY-MM-DD (non-editable field for Guess Price)
  const firstDayOfNextMonth = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const next = new Date(y, m + 1, 1);
    const mm = String(next.getMonth() + 1).padStart(2, "0");
    const dd = String(next.getDate()).padStart(2, "0");
    return `${next.getFullYear()}-${mm}-${dd}`;
  }, []);

  // helper to get markets by country code
  function marketsByCountry(code) {
    const c = countries.find((x) => x.code === code);
    return c ? c.markets : [];
  }

  useEffect(() => {
    // when category changes, set first commodity as selected
    setSelectedCommodity(selectedCategory.examples[0]);
    setSelectedAction(null);
  }, [selectedCategory]);

  useEffect(() => {
    // reset trade form when mainTab changes (important for import/export)
    setTradeForm((s) => ({ ...s, sourceCountry: "", destinationCountry: "", sourcePort: "", destinationPort: "", tentativeDeliveryDate: "" }));
  }, [mainTab]);

  // handle trade form change
  const onTradeChange = (k, v) => setTradeForm((s) => ({ ...s, [k]: v }));

  const submitTrade = (type) => {
    // type: Buy/Sell/FutureOrder
    const payload = {
      type,
      mainTab,
      category: selectedCategory.name,
      commodity: selectedCommodity,
      ...tradeForm,
    };
    // TODO: replace with backend integration
    // console.log("Submitting trade:", payload);
    alert("Submitted " + type + " (check console).\nDummy integration.\nPayload logged to console.");
    // reset some fields
    setTradeForm((s) => ({ ...s, buyerName: "", sellerName: "", address: "", contactNumber: "", email: "", qty: "", remarks: "" }));
  };

  const submitCollectionCenter = () => {
    // console.log("Collection center submit:", colForm);
    alert("Collection Center form submitted (check console).\nYou can integrate API here.");
  };

  const submitGuess = () => {
    // console.log("Guess price submit:", { ...guessForm, date: firstDayOfNextMonth, commodity: selectedCommodity });
    alert("Guess price submitted (dummy). Check console for payload.");
  };

  // winners for last month (dynamic filter)
  const filteredWinners = useMemo(() => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return winners.filter((w) => {
      const d = new Date(w.date);
      return d >= lastMonth && d <= lastMonthEnd;
    });
  }, [winners]);

  return (
    <div className="min-h-screen bg-black p-4">
      <div className="mx-auto bg-black rounded-2xl shadow-md overflow-hidden">
        <div className="p-4">
          <h1 className="text-xl font-semibold">AgriLink — Global Commodity Wholesale Market & Connect</h1>
          {/* <p className="text-xs text-white mt-1">Mobile first · Dummy data · Integrate backend later</p> */}

          {/* Main Tabs */}
          <div className="mt-4">
            <div className="bg-black border border-green-600 py-2 px-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-0 rounded-lg place-items-center">
                {MAIN_TABS.map((t) => (
                <button
                    key={t}
                    onClick={() => {
                        setMainTab(t);
                        setSelectedAction(null);
                        setSelectedCategory(categories[0]);
                    }}
                    className={`px-1 py-2 rounded-lg text-sm font-medium text-center transition-all duration-200 whitespace-normal break-words leading-snug ${
                        mainTab === t
                        ? "bg-green-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    >
                    {t}
                </button>

                ))}
            </div>
          </div>


          {/* Breadcrumb header showing selection */}
          <div className="mt-4 px-4 py-2 flex items-center justify-between">
            <div>
              {/* <div className="text-sm text-white">Selected</div> */}
              <div className="text-white text-sm font-semibold">{mainTab} {selectedCategory ? `› ${selectedCategory.name}` : ''} {selectedCommodity ? `› ${selectedCommodity}` : ''}</div>
            </div>
            {/* <div className="text-xs text-white">AgriLink v1 • Demo</div> */}
          </div>

          {/* If mainTab is Collection Center show collection form */}
          {mainTab === "Collection Center" ? (
            <div className="mt-4 px-4 py-2 bg-black border border-green-600 p-3 rounded-lg">
              <h2 className="font-medium text-yellow-200">Collection Center — Register / Add Farmer</h2>
              <div className="mt-3 space-y-2">
                <label className="block text-xs ">Country</label>
                <select className="h-10 w-full p-2 rounded border" value={colForm.country} onChange={(e) => { const country = e.target.value; const place = collectionCentersData.find(c=>c.country===country)?.places?.[0]?.place || ''; const center = collectionCentersData.find(c=>c.country===country)?.places?.[0]?.centers?.[0] || ''; setColForm(c => ({ ...c, country, place, center })); }}>
                  {collectionCentersData.map((c) => <option key={c.country} value={c.country}>{c.country}</option>)}
                </select>

                <label className="block text-xs ">Place / State</label>
                <select className="h-10 w-full p-2 rounded border" value={colForm.place} onChange={(e) => { const place = e.target.value; const centers = collectionCentersData.find(c=>c.country===colForm.country)?.places?.find(p=>p.place===place)?.centers || []; setColForm(c => ({ ...c, place, center: centers[0] || '' })); }}>
                  {(collectionCentersData.find(c=>c.country===colForm.country)?.places || []).map(p => <option key={p.place} value={p.place}>{p.place}</option>)}
                </select>

                <label className="block text-xs ">Collection Center</label>
                <select className="h-10 w-full p-2 rounded border" value={colForm.center} onChange={(e) => setColForm(c => ({ ...c, center: e.target.value }))}>
                  {(collectionCentersData.find(c=>c.country===colForm.country)?.places?.find(p=>p.place===colForm.place)?.centers || []).map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>

                <label className="block text-xs ">Farmer / Seller / Aggregator Name</label>
                <input value={colForm.name} onChange={(e)=>setColForm(c=>({...c,name:e.target.value}))} className="w-full p-2 rounded border" placeholder="Name" />

                <label className="block text-xs ">Role</label>
                <select value={colForm.role} onChange={(e)=>setColForm(c=>({...c,role:e.target.value}))} className="h-10 w-full p-2 rounded border">
                  <option>Farmer</option>
                  <option>Seller</option>
                  <option>Aggregator</option>
                </select>

                <label className="block text-xs ">Address</label>
                <input value={colForm.address} onChange={(e)=>setColForm(c=>({...c,address:e.target.value}))} className="text-black w-full p-2 rounded border" placeholder="Address" />

                <div className="grid grid-cols-2 gap-2 place-items-center">
                  <div>
                    <label className="block text-xs ">Contact Number</label>
                    <input value={colForm.contactNumber} onChange={(e)=>setColForm(c=>({...c,contactNumber:e.target.value}))} className="w-full p-2 rounded border" placeholder="Mobile" />
                  </div>
                  <div>
                    <label className="block text-xs ">Email</label>
                    <input value={colForm.email} onChange={(e)=>setColForm(c=>({...c,email:e.target.value}))} className="w-full p-2 rounded border" placeholder="Email" />
                  </div>
                </div>

                <label className="block text-xs ">Remarks</label>
                <textarea value={colForm.remarks} onChange={(e)=>setColForm(c=>({...c,remarks:e.target.value}))} style={{height: "250px"}}className="bg-white text-black w-full p-2 rounded border" rows={3}></textarea>

                <div className="flex justify-center">
                  <button onClick={submitCollectionCenter} className="w-40 px-4 py-2 bg-green-600 text-white rounded">Submit</button>
                </div>
              </div>
            </div>
          ) : (
            // For other main tabs: show categories -> commodities -> actions
            <>
              {/* Categories list */}
              <div className="mt-4">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 bg-black border border-green-600 px-4 py-2 place-items-center">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex flex-col bg-green-600 items-center p-2 rounded-lg text-center text-xs ${selectedCategory.id === cat.id ? 'bg-green-600 text-white' : 'bg-gray-100 text-white'}`}
                    >
                      <div className="text-2xl">{cat.icon}</div>
                      <div className="mt-1 text-xs">{cat.name.split(' ')[0]}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Commodities under selected category */}
              <div className="mt-4 bg-black border border-green-600 py-2 px-4">
                <h3 className="text-lg font-medium ">Commodities in {selectedCategory.name}</h3>
                <div className="mt-2 space-y-2">
                  {selectedCategory.examples.map((ex) => (
                    <div key={ex} className="flex items-center justify-between bg-black border border-green-100 p-2 rounded">
                      <div>
                        <div className="font-medium text-yellow-200">{ex}</div>
                        <div className="text-xs text-white">{selectedCategory.name}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button onClick={() => { setSelectedCommodity(ex); setSelectedAction(null); }} className="px-3 py-1 text-xs bg-green-700 border rounded">Open</button>
                        <button onClick={() => { setSelectedCommodity(ex); setSelectedAction('Price List'); }} className="px-3 py-1 text-xs bg-green-700 border rounded">Price</button>
                        <button onClick={() => { setSelectedCommodity(ex); setSelectedAction('Guess Price'); }} className="px-3 py-1 text-xs bg-green-700 border rounded">Guess</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action row when commodity selected */}
              <div className="mt-4 bg-black border border-green-600 py-2 px-4">
                <h4 className="text-sm text-yellow-200 font-medium">Selected Commodity: <span className="font-semibold">{selectedCommodity}</span></h4>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 place-items-center">
                  {['Bulk Buy', 'Bulk Sell', 'Future Order', 'Price List', 'Guess Price', 'Winners'].map((a) => (
                    <button key={a} onClick={() => setSelectedAction(a)} className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm ${selectedAction === a ? 'bg-green-600 text-white' : 'bg-green-700 text-white'}`}>{a}</button>
                  ))}
                </div>
              </div>

              {/* Action Panels */}
              <div className="mt-4 bg-gray-600 py-2 px-4">
                {selectedAction === 'Price List' && (
                  <div className="bg-gray-800 p-3 rounded">
                    <h5 className="font-medium text-yellow-200">Price List — {selectedCommodity}</h5>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-white bg-blue-600 border-b">
                            <th className="py-2">Commodity</th>
                            <th className="py-2">Market</th>
                            <th className="py-2">Country</th>
                            <th className="py-2">Unit</th>
                            <th className="py-2">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {priceList.filter(p => p.commodity === selectedCommodity).length === 0 ? (
                            <tr><td colSpan={5} className="py-3 text-xs text-white">No price records found for {selectedCommodity}. Showing sample prices.</td></tr>
                          ) : null}
                          {priceList.filter(p => p.commodity === selectedCommodity).map((p, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2">{p.commodity}</td>
                              <td className="py-2">{p.market}</td>
                              <td className="py-2">{p.country}</td>
                              <td className="py-2">{p.unit}</td>
                              <td className="py-2">{p.price}</td>
                            </tr>
                          ))}
                          {/* sample fallback */}
                          {priceList.filter(p => p.commodity === selectedCommodity).length === 0 && (
                            dummyPriceList.slice(0,2).map((p, i) => (
                              <tr key={`s${i}`} className="border-b">
                                <td className="py-2">{p.commodity}</td>
                                <td className="py-2">{p.market}</td>
                                <td className="py-2">{p.country}</td>
                                <td className="py-2">{p.unit}</td>
                                <td className="py-2">{p.price}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(selectedAction === 'Guess Price' || mainTab === 'Guess Price') && (
                  <div className="bg-black p-3 rounded">
                    <h5 className="font-medium text-yellow-200">Guess Price — {selectedCommodity}</h5>
                    <div className="mt-3 space-y-2">
                      <label className="text-xs">Country</label>
                      <select className="w-full p-2 rounded border" value={guessForm.country} onChange={(e)=> setGuessForm(g=>({...g,country:e.target.value, market: marketsByCountry(e.target.value)[0]}))}>
                        {countries.map(c=> <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>

                      <label className="text-xs">Market</label>
                      <select className="w-full p-2 rounded border" value={guessForm.market} onChange={(e)=> setGuessForm(g=>({...g,market:e.target.value}))}>
                        {marketsByCountry(guessForm.country).map(m=> <option key={m}>{m}</option>)}
                      </select>

                      <label className="text-xs">1st Day of Next Month</label>
                      <input className="w-full p-2 rounded border bg-white" value={firstDayOfNextMonth} readOnly />

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 place-items-center">
                        <div>
                          <label className="text-xs">Guess Price Rate</label>
                          <input value={guessForm.guessRate} onChange={(e)=> setGuessForm(g=>({...g,guessRate:e.target.value}))} className="w-full p-2 rounded border" placeholder="Rate" />
                        </div>
                        <div>
                          <label className="text-xs">Guess Price Per Unit</label>
                          <input value={guessForm.guessPerUnit} onChange={(e)=> setGuessForm(g=>({...g,guessPerUnit:e.target.value}))} className="w-full p-2 rounded border" placeholder="Per Unit" />
                        </div>
                        <div>
                          <label className="text-xs">Variation %</label>
                          <input value={guessForm.variation} onChange={(e)=> setGuessForm(g=>({...g,variation:e.target.value}))} className="w-full p-2 rounded border" placeholder="%" />
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <button onClick={submitGuess} className="w-40 px-4 py-2 bg-green-600 text-white rounded">Submit Guess</button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedAction === 'Winners' && (
                  <div className="bg-gray-500 p-3 rounded">
                    <h5 className="font-medium text-yellow-200">Winners — Last Month</h5>
                    <div className="mt-2">
                      {filteredWinners.length === 0 ? (
                        <div className="text-xs text-white">No winners in the last month.</div>
                      ) : (
                        <ul className="space-y-2">
                          {filteredWinners.map((w, idx) => (
                            <li key={idx} className="p-2 bg-blue-600 rounded shadow-sm flex items-center justify-between">
                              <div>
                                <div className="font-medium text-white">{w.name}</div>
                                <div className="text-xs text-white">{w.commodity} • {w.market} • {w.date}</div>
                              </div>
                              <div className="text-sm font-semibold text-white">{w.prize}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {['Bulk Buy', 'Bulk Sell', 'Future Order'].includes(selectedAction) && (
                  <div className="bg-gray-500 p-3 rounded">
                    <h5 className="font-medium text-yellow-200">{selectedAction} — {selectedCommodity}</h5>
                    <div className="mt-3 space-y-2">
                      {/* Show selected labels */}
                      <div className="text-xs text-white">Context: {mainTab} / {selectedCategory.name} / {selectedCommodity}</div>

                      <div className="grid grid-cols-1 gap-2 place-items-center">
                        <input value={tradeForm.buyerName} onChange={(e)=>onTradeChange('buyerName', e.target.value)} placeholder="Buyer Name" className="w-full p-2 rounded border" />
                        <input value={tradeForm.sellerName} onChange={(e)=>onTradeChange('sellerName', e.target.value)} placeholder="Seller Name" className="w-full p-2 rounded border" />
                        <input value={tradeForm.address} onChange={(e)=>onTradeChange('address', e.target.value)} placeholder="Address" className="w-full p-2 rounded border" />

                        <div className="grid grid-cols-2 gap-2 place-items-center">
                          <input value={tradeForm.contactNumber} onChange={(e)=>onTradeChange('contactNumber', e.target.value)} placeholder="Contact Number" className="w-full p-2 rounded border" />
                          <input value={tradeForm.email} onChange={(e)=>onTradeChange('email', e.target.value)} placeholder="Email" className="w-full p-2 rounded border" />
                        </div>

                        <div className="grid grid-cols-2 gap-2 place-items-center">
                          <input value={tradeForm.qty} onChange={(e)=>onTradeChange('qty', e.target.value)} placeholder="Qty" className="w-full p-2 rounded border" />
                          <select value={tradeForm.unit} onChange={(e)=>onTradeChange('unit', e.target.value)} className="w-full p-2 rounded border">
                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>

                        {/* If mainTab Import or Export show extra fields */}
                        {(mainTab === 'Import' || mainTab === 'Export') && (
                          <>
                            <div className="grid grid-cols-2 gap-2 place-items-center">
                              <select value={tradeForm.sourceCountry} onChange={(e)=>onTradeChange('sourceCountry', e.target.value)} className="p-2 rounded border">
                                <option value="">Source Country</option>
                                {countries.map(c=> <option key={c.code} value={c.name}>{c.name}</option>)}
                              </select>
                              <select value={tradeForm.destinationCountry} onChange={(e)=>onTradeChange('destinationCountry', e.target.value)} className="p-2 rounded border">
                                <option value="">Destination Country</option>
                                {countries.map(c=> <option key={c.code} value={c.name}>{c.name}</option>)}
                              </select>
                            </div>

                            <div className="grid grid-cols-2 gap-2 place-items-center">
                              <input value={tradeForm.sourcePort} onChange={(e)=>onTradeChange('sourcePort', e.target.value)} placeholder="Source Port" className="w-full p-2 rounded border" />
                              <input value={tradeForm.destinationPort} onChange={(e)=>onTradeChange('destinationPort', e.target.value)} placeholder="Destination Port" className="w-full p-2 rounded border" />
                            </div>

                            <div>
                              <label className="text-xs">Tentative Date of Delivery</label>
                              <input type="date" value={tradeForm.tentativeDeliveryDate} onChange={(e)=>onTradeChange('tentativeDeliveryDate', e.target.value)} className="w-full p-2 rounded border" />
                            </div>
                          </>
                        )}

                        <textarea value={tradeForm.remarks} onChange={(e)=>onTradeChange('remarks', e.target.value)} style={{height: "250px"}} className="w-full bg-white text-black p-2 rounded border" rows={3} placeholder="Remarks"></textarea>

                        <div className="flex justify-between">
                          <button onClick={()=>{ setTradeForm({ buyerName: '', sellerName: '', address: '', contactNumber: '', email: '', qty: '', unit: UNIT_OPTIONS[0], sourceCountry: '', destinationCountry: '', sourcePort: '', destinationPort: '', tentativeDeliveryDate: '', remarks: '' }); alert('Form reset'); }} className="px-3 py-2 rounded border text-sm">Reset</button>
                          <button onClick={()=>submitTrade(selectedAction)} className="px-4 py-2 bg-green-600 text-white rounded">Submit {selectedAction}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>

            </>
          )}

          <footer className="bg-gray-700 text-white py-2 px-4 sm:px-6 md:px-20 mt-4">
            <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
                <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
                <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
                <Link to="/about-us" className="text-white hover:text-green-400 transition">About</Link>
                <Link to="/privacy-policy" className="text-white hover:text-green-400 transition">Privacy Policy</Link>
                <Link to="/terms-of-use" className="text-white hover:text-green-400 transition">Terms of Use</Link>
                <Link to="/contact-us" className="text-white hover:text-green-400 transition">Contact</Link>
                </div>
            </div>
          </footer>

        </div>
      </div>
    </div>
  );
}
